import React from 'react';
//Constants
import { GRADE, LABELS } from '../constants/general';
import { ResumeEducationDetailValidationSchema } from '../constants/schemas';
//Utils
import { addMultipleEducationDetails } from '../middleware/educationDetails';
import { getGradeCode, trimStringsInObject } from '../utils/general';
//Components
import Label from './Label';
//Utility Packages
import { FieldArray, Formik } from 'formik';
import moment from 'moment';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Editor } from 'primereact/editor';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import DatePicker from 'react-datepicker';
import { confirmDialog } from 'primereact/confirmdialog';
import { Tooltip as ReactTooltip } from 'react-tooltip';
//Redux
import { dispatch } from '../utils/store';
import { useSelector } from 'react-redux';
import { Actions } from '../redux/actions';
// images
import deleteIcon from '../assets/images/grid-delete-icon.svg';

function EducationDetailForm({ isEditable, setIsEditable }) {
  //Store
  const toastReff = useSelector((state) => state.notifications.toastReff);
  const { resumeData, educationDetails } = useSelector((state) => state.resumeFetchDetails);
  const initialValues = {
    educations: resumeData.Education.map((edu) => {
      return {
        details: edu?.details || '',
        course: edu?.course || '',
        university: edu?.university || '',
        startDate: edu.startDate ? moment(new Date(edu.startDate)).startOf('month').toDate() : null,
        endDate: edu.endDate ? moment(new Date(edu.endDate)).startOf('month').toDate() : null,
        grade: GRADE.find((g) => g.code === getGradeCode(edu?.grade)),
        remarks: edu?.remarks || ''
      };
    })
  };
  const handleSubmit = (values) => {
    const payload = values.educations.map((edu) => {
      return {
        ...trimStringsInObject({
          ...edu,
          grade: edu?.grade?.code || null
        }),
        startDate: edu?.startDate ? moment(edu?.startDate).format('YYYY-MM-DD') : null,
        endDate: edu?.endDate ? moment(edu?.endDate).format('YYYY-MM-DD') : null
      };
    });
    const handleSave = () => {
      dispatch(Actions.ResumeFetchDetails.SetEducationDetails, values);
      setIsEditable(true);
    };
    addMultipleEducationDetails(payload, handleSave, toastReff);
  };

  const handleDeleteClick = (formik, index) => {
    confirmDialog({
      header: 'Delete',
      message: 'Are you sure you want to delete?',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'accept',
      accept: () => {
        const updatedEducations = formik.values.educations.filter((_, i) => i !== index);
        formik.setFieldValue('educations', updatedEducations);
      }
    });
  };
  return (
    <div className="resume-education-details mt-3">
      <Formik
        initialValues={educationDetails ? educationDetails : initialValues}
        enableReinitialize={true}
        validationSchema={ResumeEducationDetailValidationSchema}
        onSubmit={(values) => handleSubmit(values)}>
        {(formik) => (
          <form onSubmit={formik.handleSubmit} className="p-fluid">
            <FieldArray name="educations">
              {() => (
                <div>
                  {formik.values.educations.map((_, index) => (
                    <div key={index} className="project-title">
                      <div className="details-header">
                        <h3 className="mb-3">Education {index + 1}</h3>
                        {formik.values.educations.length > 1 && !isEditable && (
                          <div>
                            <Button
                              type="button"
                              data-tooltip-id="delete-button"
                              data-tooltip-content="Delete"
                              rounded
                              outlined
                              severity="secondary"
                              aria-label="Delete"
                              onClick={() => handleDeleteClick(formik, index)}
                              className="grid-action-btn">
                              <em>
                                <img src={deleteIcon} alt="delete icon" />
                              </em>
                            </Button>
                            <ReactTooltip id="delete-button" place="top" />
                          </div>
                        )}
                      </div>
                      <EducationForm formik={formik} index={index} isEditable={isEditable} />
                      {index < formik.values.educations.length - 1 && <hr className="mt-7 mb-5" />}
                    </div>
                  ))}
                </div>
              )}
            </FieldArray>
            <div className="submit-btn-block">
              {!isEditable && (
                <Button type="submit" variant="contained">
                  {LABELS.BUTTON.SAVE}
                </Button>
              )}
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

const EducationForm = ({ formik, index, isEditable }) => {
  const field = (name) => `educations[${index}].${name}`;

  const getIn = (obj, path) => {
    return path
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
  };

  const isFormFieldValid = (formik, name) =>
    !!(getIn(formik.errors, name) && getIn(formik.touched, name));

  const getFormErrorMessage = (formik, name) => {
    const error = getIn(formik.errors, name);
    const touched = getIn(formik.touched, name);
    return touched && error ? <small className="p-error">{error}</small> : null;
  };

  return (
    <div className="form-row-wrapper">
      <div className="form-col">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('details')} text={LABELS.INPUT.EDUCATION_DETAILS} isBold />
            <InputText
              id={field('details')}
              name={field('details')}
              placeholder={LABELS.INPUT.EDUCATION_DETAILS}
              value={getIn(formik.values, field('details'))}
              onChange={formik.handleChange}
              disabled={isEditable}
              className={classNames({ 'p-invalid': isFormFieldValid(formik, field('details')) })}
            />
          </div>
          {getFormErrorMessage(formik, field('details'))}
        </div>
      </div>

      <div className="form-col">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('course')} text={LABELS.INPUT.COURSE} isBold />
            <InputText
              id={field('course')}
              name={field('course')}
              placeholder={LABELS.INPUT.COURSE}
              value={getIn(formik.values, field('course'))}
              onChange={formik.handleChange}
              disabled={isEditable}
              className={classNames({ 'p-invalid': isFormFieldValid(formik, field('course')) })}
            />
          </div>
          {getFormErrorMessage(formik, field('course'))}
        </div>
      </div>

      <div className="form-col">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('university')} text={LABELS.INPUT.UNIVERSITY} isBold />
            <InputText
              id={field('university')}
              name={field('university')}
              placeholder={LABELS.INPUT.UNIVERSITY}
              value={getIn(formik.values, field('university'))}
              onChange={formik.handleChange}
              disabled={isEditable}
              className={classNames({ 'p-invalid': isFormFieldValid(formik, field('university')) })}
            />
          </div>
          {getFormErrorMessage(formik, field('university'))}
        </div>
      </div>

      <div className="form-col">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('grade')} text={LABELS.INPUT.GRADE} isBold />
            <Dropdown
              id={field('grade')}
              name={field('grade')}
              placeholder={LABELS.INPUT.GRADE}
              value={getIn(formik.values, field('grade'))}
              onChange={(e) => formik.setFieldValue(field('grade'), e.value)}
              options={GRADE}
              optionLabel="name"
              disabled={isEditable}
              className={classNames({ 'p-invalid': isFormFieldValid(formik, field('grade')) })}
            />
          </div>
          {getFormErrorMessage(formik, field('grade'))}
        </div>
      </div>

      <div className="form-col">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('startDate')} text={LABELS.INPUT.START_DATE} isBold />
            <DatePicker
              id={field('startDate')}
              name={field('startDate')}
              dateFormat="MM/yyyy"
              placeholderText="Select Start Date"
              showMonthYearPicker
              selected={getIn(formik.values, field('startDate'))}
              onChange={(date) => formik.setFieldValue(field('startDate'), date)}
              maxDate={new Date()}
              onKeyDown={(e) => e.preventDefault()}
              disabled={isEditable}
              className={classNames({ 'p-invalid': isFormFieldValid(formik, field('startDate')) })}
            />
          </div>
          {getFormErrorMessage(formik, field('startDate'))}
        </div>
      </div>

      <div className="form-col">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('endDate')} text={LABELS.INPUT.END_DATE} isBold />
            <DatePicker
              id={field('endDate')}
              name={field('endDate')}
              dateFormat="MM/yyyy"
              placeholderText="Select End Date"
              showMonthYearPicker
              selected={getIn(formik.values, field('endDate'))}
              onChange={(date) => formik.setFieldValue(field('endDate'), date)}
              maxDate={new Date()}
              onKeyDown={(e) => e.preventDefault()}
              disabled={isEditable}
              className={classNames({ 'p-invalid': isFormFieldValid(formik, field('endDate')) })}
            />
          </div>
          {getFormErrorMessage(formik, field('endDate'))}
        </div>
      </div>

      <div className="form-col full-width">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('remarks')} text={LABELS.INPUT.REMARKS} isBold />
            {isEditable && (
              <Editor
                id={field('remarks')}
                name={field('remarks')}
                value={getIn(formik.values, field('remarks'))}
                onTextChange={(e) => formik.setFieldValue(field('remarks'), e.htmlValue)}
                style={{ height: '260px' }}
                readOnly={true}
                className={classNames({ 'p-invalid': isFormFieldValid(formik, field('remarks')) })}
              />
            )}
            {!isEditable && (
              <Editor
                id={field('remarks')}
                name={field('remarks')}
                value={getIn(formik.values, field('remarks'))}
                onTextChange={(e) => formik.setFieldValue(field('remarks'), e.htmlValue)}
                style={{ height: '260px' }}
                className={classNames({ 'p-invalid': isFormFieldValid(formik, field('remarks')) })}
              />
            )}
          </div>
          {getFormErrorMessage(formik, field('remarks'))}
        </div>
      </div>
    </div>
  );
};

export default EducationDetailForm;
